import * as ROUTES from '../common/routes';
import * as yaml from 'js-yaml';
import Button from './Button';
import React from 'react';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { getText, getPath, getReporterName } from '../common/utils/helpers';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslations } from '../common/hooks/useTranslations';

const urlPrefix = getPath();

interface IProps {
  data: any;
  defaultImage?: any;
  showTheme?: boolean;
}

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      answerContainerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      answerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '26px',
      },
      imageContainerStyle: {
        width: '60px',
        minWidth: '60px',
        height: '60px',
        overflow: 'hidden',
        background: COLORS.circleGray,
        marginRight: '10px',
        borderRadius: '8px',
      },
      imageStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'top',
      },
      textContainerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
      },
      textWrapperStyle: {
        display: 'flex',
        flexDirection: 'column',
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '16px',
        lineHeight: '20px',
        fontFamily: FONTS.alegreya,
        color: COLORS.textBlack,
      },
      textStyle: {
        '& b': {
          fontWeight: FONT_WEIGHT.bold,
        },
        '& span': {
          color: COLORS.textGray,
          padding: '0 6px',
        },
      },
      introTextStyle: {
        marginTop: '6px',
        width: '100%',
        height: '40px',
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '16px',
        lineHeight: '20px',
      },
      buttonStyle: {
        width: '140px',
        minWidth: '140px',
        background: COLORS.appButtonsLightGray,
        border: `1px solid ${COLORS.appButtonsGray}`,
        margin: '0 0 0 10px',
        boxSizing: 'border-box',
      },
      headerContainerStyle: {
        marginBottom: '12px',
        fontSize: '16px',
        lineHeight: '19px',
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        '& b': {
          fontWeight: FONT_WEIGHT.bold,
        },
      },
      [`@media (max-width: 840px)`]: {
        textContainerStyle: {
          flexDirection: 'column',
        },
        buttonStyle: {
          margin: '10px 0 0 0',
        },
      },
    })
);

const SurveysList = ({
  data,
  defaultImage,
  showTheme,
}: IProps): JSX.Element => {
  const classes = useStyles();
  const { search, pathname } = useLocation();
  const lng = useSelector((state: any) => state.app.lng);
  const navigate = useNavigate();

  const openLabel = useTranslations('read_button');

  const getItems = (item, index) => {
    const rawIntroText: string = getText(item?.content, 'intro', lng);
    const img = item?.images?.[0].url;
    const imageUrl: string = `${urlPrefix}${img}`;

    const getYamlData = (data) => {
      if (!data) {
        return null;
      }

      try {
        const convertedData = yaml.load(data);

        return convertedData;
      } catch (error) {
        console.log('>>>', error);
      }
    };

    const myselfData = getYamlData(item.content.myself);
    const otherData = getYamlData(item.content.other);
    const surveyData = getYamlData(item.content.survey);

    const date = item.content.date || '';
    const writerName = getReporterName(myselfData, 'Vārds', 'Uzvārds');
    const reporterName = getReporterName(
      otherData,
      'Intervējamā vārds',
      'Intervējamā uzvārds'
    );

    const visibleName = reporterName || writerName || '';

    const text = `<b>${visibleName}</b><span>${date}</span>`;

    const onClick = (event: React.SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();

      navigate({
        pathname: `${ROUTES.THEME}/${item.content.themeid}/${item.content.surveyid}/${item.content.uid}`,
        search,
      });
    };

    const introText = rawIntroText || surveyData[0]?.data || '';
    const thumbImageUrl = img ? imageUrl : defaultImage;

    const getThemeLabel = () => {
      const text: string = `<span><b>Tēma:</b> ${item?.content?.themetitle}. <b>Anketa:</b> ${item?.content?.surveytitle}</span>`;

      return (
        <div
          className={classes.headerContainerStyle}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      );
    };

    return (
      <div className={classes.answerContainerStyle} key={index}>
        {showTheme && getThemeLabel()}
        <div className={classes.answerStyle}>
          <div className={classes.imageContainerStyle}>
            <img src={thumbImageUrl} alt="" className={classes.imageStyle} />
          </div>
          <div className={classes.textContainerStyle}>
            <div className={classes.textWrapperStyle}>
              <div
                className={classes.textStyle}
                dangerouslySetInnerHTML={{ __html: text }}
              />
              <div
                className={classes.introTextStyle}
                dangerouslySetInnerHTML={{ __html: introText }}
              />
            </div>
            <Button
              text={openLabel}
              onClick={onClick}
              className={classes.buttonStyle}
            />
          </div>
        </div>
      </div>
    );
  };

  return data.map(getItems);
};

export default SurveysList;
